import { useQuery, useQueryClient, type QueryClient } from 'react-query';

import { useCompany } from 'modules/app/hooks/useCompany';
import { getFetcher } from 'src/core/api/fetcher';
import { unwrapQuery } from 'src/core/api/unwrapQuery';

import {
  type LoadCustomFieldValuesByIdsRawResponse,
  LOAD_CUSTOM_FIELDS_VALUES_BY_IDS_MULTIPLE,
} from './queries/queries';
import { useApprovalWorkflowQuery } from './useApprovalWorkflowQuery';
import {
  type ApprovalWorkflow,
  type ApprovalWorkflowNode,
} from '../approvalWorkflow';

export type CustomFieldValues = Record<string, string>;

export const useApprovalWorkflowCustomFieldValuesQuery = () => {
  const company = useCompany();
  const queryClient = useQueryClient();
  const approvalWorkflowQuery = useApprovalWorkflowQuery();
  const approvalWorkflow = unwrapQuery(approvalWorkflowQuery);

  return useQuery(
    ['approval-workflows', 'active', 'custom-field-values'],
    async (): Promise<CustomFieldValues> => {
      if (!approvalWorkflow) {
        throw new Error('Approval workflow not found');
      }

      const { customFieldIds, customFieldValueIds } =
        extractCustomFieldIds(approvalWorkflow);

      const response: { id: string; name: string }[] =
        await fetchCustomFieldValues({
          companyId: company.id,
          customFieldIds,
          customFieldValueIds,
          queryClient,
        });
      return Object.fromEntries(
        response.map((value) => [value.id, value.name]),
      );
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(approvalWorkflow),
    },
  );
};

const fetchCustomFieldValues = async ({
  companyId,
  customFieldIds,
  customFieldValueIds,
  queryClient,
}: {
  companyId: string;
  customFieldIds: string[];
  customFieldValueIds: string[];
  queryClient: QueryClient;
}) => {
  const data = await queryClient.fetchQuery(
    [
      'useLoadCustomFieldValuesByIds',
      companyId,
      customFieldIds,
      customFieldValueIds,
    ],
    getFetcher<LoadCustomFieldValuesByIdsRawResponse>({
      companyId,
      getRequest: () => ({
        type: 'graphQL',
        target: 'v2',
        query: LOAD_CUSTOM_FIELDS_VALUES_BY_IDS_MULTIPLE,
        variables: {
          customFieldIds,
          customFieldValueIds,
        },
        method: 'get',
      }),
    }),
    {
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    },
  );
  return data.company.customFields.flatMap((customField) => {
    if (customField.__typename === 'CustomFieldList') {
      return reshapeValues(customField.values.edges);
    }
    if (customField.__typename === 'CustomFieldBoolean') {
      return customField.booleanValues.map((value) => ({
        id: value.id,
        name: String(value.name),
      }));
    }
    return [];
  });
};

export const extractCustomFieldIds = (
  approvalWorkflow: ApprovalWorkflow,
): { customFieldIds: string[]; customFieldValueIds: string[] } => {
  const customFieldIds: string[] = [];
  const customFieldValueIds: string[] = [];

  const traverseNode = (node: ApprovalWorkflowNode) => {
    if (node.values && Array.isArray(node.values)) {
      const validValues = node.values.filter(
        (value): value is string => value !== null,
      );
      customFieldValueIds.push(...validValues);
    }
    if (node.branch) {
      const { dimension, nodes } = node.branch;
      if (dimension.type === 'customField') {
        customFieldIds.push(dimension.customFieldId);
      }
      nodes.forEach(traverseNode);
    }
  };
  traverseNode(approvalWorkflow.definition);
  return {
    customFieldIds: [...new Set(customFieldIds)],
    customFieldValueIds: [...new Set(customFieldValueIds)],
  };
};

const reshapeValues = (
  edges: { node: { id: string; name: string } }[],
): { id: string; name: string }[] => {
  return edges.map((edge) => ({
    id: edge.node.id,
    name: edge.node.name,
  }));
};
